/**
 * Pure builders for the host:context snapshot published into the Aristotle
 * iframe. HARD RULE: whitelist entity IDENTITY fields only — never forward
 * location.state or Redux rows wholesale (they can carry SSNs and other row
 * data); Aristotle fetches data through its own integration under its own
 * authorization.
 */

/** Map the Redux selectedProfile to the entity identity whitelist, or null. */
export const buildEntityFromProfile = (profile) => {
    if (!profile || !profile.type) return null;
    const uid = profile.studentUID ?? profile.facultyID ?? profile.prospectId ?? null;
    const entity = { type: String(profile.type) };
    if (profile.profileId !== undefined && profile.profileId !== null && profile.profileId !== '') {
        entity.id = String(profile.profileId);
    }
    if (uid !== null && uid !== undefined && Number(uid) !== 0) {
        entity.uid = String(uid);
    }
    if (profile.profileName) {
        entity.label = String(profile.profileName);
    }
    return entity.id || entity.uid || entity.label ? entity : null;
};

/**
 * Resolve a display name for the current path from the sidebar menu tree.
 * CAMSMenu pads menuItemName with leading spaces to encode depth — always trim.
 */
export const resolvePageName = (sideBarItems, pathname) => {
    if (!pathname) return null;
    const stack = Array.isArray(sideBarItems) ? [...sideBarItems] : [];
    while (stack.length > 0) {
        const item = stack.pop();
        if (!item) continue;
        if (item.url === pathname) {
            const name = (item.menuItemName || item.name || '').trim();
            if (name) return name;
        }
        if (Array.isArray(item.subLinks)) {
            stack.push(...item.subLinks);
        }
    }
    return null;
};

/** Fallback page name from the path: "/admissions/student-status" -> "Student Status". */
export const humanizePath = (pathname) => {
    const segment = (pathname || '').split('/').filter(Boolean).pop() || '';
    if (!segment) return null;
    return segment
        .replace(/[-_]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Build the full host:context payload. Pure — every input is a parameter so
 * this is unit-testable without a DOM or store.
 */
export const buildPageContext = ({ href, pathname, sideBarItems, selectedProfile, user, product, tenantHost }) => {
    const context = {
        url: href,
        pathname,
        pageName: resolvePageName(sideBarItems, pathname) || humanizePath(pathname),
        product,
        tenantHost,
    };
    const entity = buildEntityFromProfile(selectedProfile);
    if (entity) {
        context.entity = entity;
    }
    if (user && (user.username || user.userId)) {
        context.user = user;
    }
    return context;
};

/** Identity snapshot of the signed-in SIS user from auth-service localStorage.
 *  NOTE: 'user' is the login username; 'username' is the display name. */
export const buildUserFromLocalStorage = () => {
    const username = localStorage.getItem('user') || '';
    const userId = localStorage.getItem('userId') || '';
    if (!username && !userId) return null;
    return {
        username,
        userId,
        campusId: localStorage.getItem('campusID') || '',
        superUser: localStorage.getItem('superUser') === 'true',
    };
};
